import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, Building2, ShieldCheck, MessageSquare, Kanban, ArrowRight, Target, Sparkles, LineChart, Mail } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1565043534426-8a67ac8671e2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

const FEATURES = [
  { icon: Layers, title: "Parallel deal origination", body: "Run extraordinary finance, generational succession, investor search and internationalisation theses simultaneously — Claude and GPT cross-check every target." },
  { icon: Building2, title: "Company intelligence", body: "Registry, financials and credit data from Global Database and Cerved, enriched with live web research. Every field carries its source and freshness." },
  { icon: ShieldCheck, title: "AI-powered due diligence", body: "A structured checklist — registry, UBO, three-year financials, M&A history, credit events, sanctions and PEPs — auto-populated and exportable as PDF." },
  { icon: MessageSquare, title: "Ask Clara AI agent", body: "Six specialist agents — research, valuation, M&A, legal, diligence and QC — reasoning over your workspace and uploaded documents. Run a Virtual Deal Committee in one line." },
  { icon: Kanban, title: "Deal pipeline management", body: "From sourcing to closing: pipeline stages, valuations, dossiers and execution projects in one institutional workspace." },
];

const STEPS = [
  { n: "01", icon: Target, title: "Define your investment thesis", body: "Sector, geography, revenue band, strategy and special situations such as succession or refinancing." },
  { n: "02", icon: Sparkles, title: "Clara finds and enriches matching companies", body: "Parallel AI origination, registry lookups and web evidence produce a verified, deduplicated target list." },
  { n: "03", icon: LineChart, title: "Analyze, score and act on deals", body: "Valuations, due diligence, committee recommendations and outreach drafts — ready for your IC." },
];

const reveal = (i = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" },
});

function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl" style={{ background: "rgba(7,13,32,0.72)", borderBottom: "1px solid rgba(197,160,89,0.12)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="landing-brand">
          <div className="w-8 h-8 gradient-gold rounded-sm flex items-center justify-center"><span className="font-display text-black font-bold">C</span></div>
          <span className="font-display text-lg tracking-tight">Clara Deals</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-[13px] text-white/60">
          <a href="#features" className="hover:text-gold transition-colors" data-testid="landing-nav-features">Platform</a>
          <a href="#how" className="hover:text-gold transition-colors" data-testid="landing-nav-how">How it works</a>
          <a href="#pricing" className="hover:text-gold transition-colors" data-testid="landing-nav-pricing">Pricing</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 text-[13px] text-white/80 hover:text-white transition-colors" data-testid="landing-signin-btn">Sign in</Link>
          <Link to="/register" className="px-4 py-2 text-[13px] rounded-sm bg-[#C5A059] text-black font-medium hover:bg-[#D4AF37] transition-colors" data-testid="landing-register-btn">Request access</Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative min-h-[92vh] flex items-end pt-28 pb-20 overflow-hidden">
      <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(100deg,#070D20 22%,rgba(7,13,32,0.82) 55%,rgba(7,13,32,0.35))" }} />
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full">
        <motion.div {...reveal(0)} className="overline text-gold mb-6 flex items-center gap-3"><span className="w-10 h-px bg-[#C5A059]" /> Institutional M&A Intelligence</motion.div>
        <motion.h1 {...reveal(1)} className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl" data-testid="landing-hero-title">
          Clara Deals — <span className="italic text-gold">AI-Powered</span> Deal Origination and M&A Intelligence
        </motion.h1>
        <motion.p {...reveal(2)} className="mt-7 text-base md:text-lg text-white/65 max-w-2xl leading-relaxed" data-testid="landing-hero-subtitle">
          Clara helps financial advisors and M&A professionals find, analyze, and act on deals faster — combining parallel AI origination, verified company data and a committee of specialist agents.
        </motion.p>
        <motion.div {...reveal(3)} className="mt-10 flex flex-wrap gap-3">
          <Link to="/register" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-sm bg-[#C5A059] text-black font-medium text-sm hover:bg-[#D4AF37] transition-colors" data-testid="landing-hero-cta">
            Request access <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/login" className="inline-flex items-center px-6 py-3.5 rounded-sm text-sm text-white border border-white/20 hover:border-[#C5A059] hover:text-gold transition-colors" data-testid="landing-hero-signin">
            Sign in to workspace
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <motion.div {...reveal()} className="lg:col-span-4 lg:sticky lg:top-28 self-start">
          <div className="overline text-gold mb-4">The platform</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">Five engines. <span className="italic text-gold">One</span> deal desk.</h2>
          <p className="mt-5 text-white/55 text-sm md:text-base leading-relaxed">Everything an origination team needs, from first signal to investment committee.</p>
        </motion.div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px" style={{ background: "rgba(197,160,89,0.14)" }}>
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} {...reveal(i)} className={`group p-8 md:p-10 bg-[#070D20] hover:bg-[#0B1530] transition-colors ${i === 0 ? "sm:col-span-2" : ""}`} data-testid={`landing-feature-${i}`}>
              <f.icon size={22} className="text-gold mb-6 transition-transform group-hover:-translate-y-0.5" strokeWidth={1.5} />
              <h3 className="font-display text-xl mb-3">{f.title}</h3>
              <p className="text-[14px] text-white/55 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="py-28 md:py-36" style={{ background: "#0B1530" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...reveal()} className="max-w-2xl mb-16">
          <div className="overline text-gold mb-4">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">From thesis to term sheet in three steps.</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-10">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} {...reveal(i)} className="relative pt-8 border-t border-[#C5A059]/40" data-testid={`landing-step-${i + 1}`}>
              <div className="font-mono text-gold text-sm mb-6">{s.n}</div>
              <s.icon size={20} className="text-white/70 mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-xl mb-3">{s.title}</h3>
              <p className="text-[14px] text-white/55 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="py-28 md:py-36">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <motion.div {...reveal()} className="relative p-10 md:p-16 overflow-hidden rounded-sm" style={{ background: "linear-gradient(135deg,#152A55,#0B1530)", border: "1px solid rgba(197,160,89,0.3)" }} data-testid="landing-pricing">
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle,rgba(197,160,89,0.25),transparent 70%)" }} />
          <div className="overline text-gold mb-4">Pricing</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight max-w-xl">Tailored plans for advisory boutiques, PE funds and family offices.</h2>
          <p className="mt-5 text-white/60 max-w-xl text-sm md:text-base">Seat-based access with usage-aware AI and data credits. Pricing is shared on request during our private access programme.</p>
          <Link to="/register" className="group mt-9 inline-flex items-center gap-2 px-6 py-3.5 rounded-sm bg-[#C5A059] text-black font-medium text-sm hover:bg-[#D4AF37] transition-colors" data-testid="landing-pricing-cta">
            Request access <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 py-14" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="font-display text-lg mb-3">Clara Deals</div>
          <a href="mailto:contact@ashbournelaw.co.uk" className="flex items-center gap-2 text-[13px] text-white/55 hover:text-gold transition-colors" data-testid="landing-contact-email">
            <Mail size={13} /> contact@ashbournelaw.co.uk
          </a>
          <div className="text-[12px] text-white/35 mt-2">Address and phone — to be announced</div>
        </div>
        <p className="md:col-span-8 text-[11.5px] leading-relaxed text-white/35" data-testid="landing-disclaimer">
          Legal disclaimer: Clara Deals provides AI-generated research and analytical tools for professional users only. Outputs — including valuations, due diligence findings and committee recommendations — are informational, may contain errors and do not constitute investment, legal, tax or accounting advice, nor an offer or solicitation to buy or sell any security. Third-party data (Global Database, Cerved, web sources) is provided as-is. Users remain solely responsible for independent verification and for decisions taken. © {new Date().getFullYear()} Clara Deals. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen text-white" style={{ background: "#070D20" }} data-testid="landing-page">
      <Nav />
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      <Footer />
    </div>
  );
}
